/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { filter } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import ReceiptFormat from '@linc-technologies/linced-common/enums/receipt-format';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import moment from 'moment';
import { mapBy, uniq } from '@linc-technologies/linced-common/utils/arrays';

export default class TransactionsController extends Controller {
  @service
  session;

  @service
  features;

  @service
  print;

  PaymentMethod = PaymentMethod;
  TransactionType = TransactionType;

  @computed('session.school.jurisdiction')
  get donationPhrasing() {
    return this.session.school.jurisdiction === Jurisdiction.AU_NSW
      ? 'deductable gifts'
      : 'donation';
  }

  @computed('model.transactions.@each.{sourceTransaction_id,type}')
  get transactions() {
    return this.model.transactions
      .filter((item) => !item.sourceTransaction_id)
      .filter((transaction) => {
        return transaction.type !== TransactionType.ADJUSTMENT;
      });
  }

  @filter('model.transactions', function (transaction) {
    if (!isEmpty(transaction.sourceTransaction_id)) {
      // Exclude child transactions.
      return false;
    }
    return [
      TransactionType.PAYMENT,
      TransactionType.TRANSFER,
      TransactionType.REFUND,
    ].includes(transaction.type);
  })
  receiptTransactions;

  @computed('receiptTransactions.@each.date')
  get transactionDates() {
    return uniq(
      mapBy(
        this.receiptTransactions
          .slice()
          .sort((a, b) => (a.date || '').localeCompare(b.date || '')),
        'date',
      ),
    );
  }

  @computed('transactionDates.[]')
  get minDate() {
    return this.transactionDates[0];
  }

  @computed('transactionDates.[]')
  get maxDate() {
    return this.transactionDates.at(-1);
  }

  @computed('minDate', 'maxDate')
  get receiptYears() {
    let startYear = moment(this.minDate, 'YYYY-MM-DD').year();
    let endYear = moment(this.maxDate, 'YYYY-MM-DD').year();
    let years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  }

  @tracked
  editingTransaction = null;
  @tracked
  isEditingTransaction = false;
  @tracked
  selectedTransactions = [];
  @tracked
  sapRefundDialogOpen = false;

  @tracked
  receiptGenerating;

  @action
  receipt(transaction) {
    this.print.receipt(
      transaction,
      this.model.accounts,
      this.get('session.school'),
      ReceiptFormat.EIGHTYMM,
    );
  }

  @action
  fullReceipt(year) {
    this.receiptGenerating = true;
    let start = `${year}-01-01`;
    let end = `${year}-12-31`;
    let transactions = this.receiptTransactions.filter((transaction) => {
      return transaction.date >= start && transaction.date <= end;
    });
    this.print.fullReceipt(
      transactions,
      this.model.accounts,
      year,
      this.get('session.school'),
      ReceiptFormat.STANDARD,
      () => {
        this.receiptGenerating = false;
      },
    );
  }

  @action
  editTransaction(transaction) {
    this.isEditingTransaction = true;
    this.editingTransaction = transaction;
  }

  @action
  closeEditTransaction() {
    this.isEditingTransaction = false;
    this.editingTransaction = null;
  }

  @action
  onTransactionDeleted(cancelledTransaction) {
    this.model.transactions.update();
    this.model.accounts.update();
    if (!isEmpty(cancelledTransaction)) {
      // Transaction was cancelled, not deleted.  Unselect it if necessary.
      this.selectedTransactions = this.selectedTransactions.filter(
        (item) => item !== cancelledTransaction,
      );
    }
  }

  @action
  toggleTransaction(transaction) {
    if (this.selectedTransactions.includes(transaction)) {
      this.selectedTransactions = this.selectedTransactions.filter(
        (item) => item !== transaction,
      );
    } else {
      this.selectedTransactions = [...this.selectedTransactions, transaction];
    }
  }

  @action
  openSapRefundDialog() {
    this.sapRefundDialogOpen = true;
  }

  @action
  closeSapRefundDialog(refresh) {
    this.sapRefundDialogOpen = false;
    if (refresh) {
      this.model.transactions.update();
      this.model.accounts.update();
      this.selectedTransactions = [];
    }
  }

  reset() {
    this.selectedTransactions = [];
  }
}
